import {
  GIFT_PASS_TAG_COLORS,
  GIFT_PASS_TAG_TEXT_MAX_LENGTH,
  type GiftPassTagColor,
} from '@bonsai/shared';
import { X } from 'lucide-react';
import type { Ref } from 'react';

import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { GiftPassTag } from '../tags/GiftPassTag';

import type { BundleTagDraft } from './bundle-form';

import './gift-pass-bundle-editor.css';

const COLOR_NAMES: Record<GiftPassTagColor, string> = {
  gold: 'Gold',
  forest: 'Forest',
  leaf: 'Leaf',
  cream: 'Cream',
};

export interface BundleTagRowProps {
  tag: BundleTagDraft;
  index: number;
  error?: string;
  inputRef: Ref<HTMLInputElement>;
  onChange: (patch: Partial<Pick<BundleTagDraft, 'text' | 'color'>>) => void;
  onRemove: () => void;
  /** Called as the caret moves, so an icon can be inserted where it is. */
  onCaret: (start: number, end: number) => void;
}

export function BundleTagRow(p: BundleTagRowProps) {
  const label = `Tag ${p.index + 1}`;
  const errorId = `bundle-tag-${p.tag.key}-error`;
  const over = p.tag.text.trim().length > GIFT_PASS_TAG_TEXT_MAX_LENGTH;
  const reportCaret = (input: HTMLInputElement) =>
    p.onCaret(input.selectionStart ?? input.value.length, input.selectionEnd ?? input.value.length);
  return (
    <div
      className={`gpb-ed-tag${p.error ? ' gpb-ed-invalid' : ''}`}
      role="group"
      aria-label={label}
    >
      <div className="gpb-ed-tag-top">
        <label className="gpb-ed-tag-text">
          <span className="nb-sr-only">{label} text</span>
          <input
            ref={p.inputRef}
            value={p.tag.text}
            placeholder="[star] Most popular"
            aria-invalid={Boolean(p.error)}
            aria-describedby={p.error ? errorId : undefined}
            onChange={(e) => {
              p.onChange({ text: e.target.value });
              reportCaret(e.target);
            }}
            onSelect={(e) => reportCaret(e.currentTarget)}
          />
          <span className={`gpb-ed-tag-count${over ? ' gpb-ed-over' : ''}`} aria-hidden>
            {p.tag.text.trim().length}/{GIFT_PASS_TAG_TEXT_MAX_LENGTH}
          </span>
        </label>
        <button
          type="button"
          className="gpb-ed-icon-btn"
          aria-label={`Remove ${label.toLowerCase()}`}
          onClick={p.onRemove}
        >
          <X size={14} aria-hidden />
        </button>
      </div>
      <div className="gpb-ed-tag-bottom">
        <div role="radiogroup" aria-label={`${label} color`} className="gpb-ed-swatches">
          {GIFT_PASS_TAG_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={p.tag.color === color}
              aria-label={COLOR_NAMES[color]}
              className={`gpb-ed-swatch gp-tag-${color}`}
              onClick={() => p.onChange({ color })}
            />
          ))}
        </div>
        {p.tag.text.trim() && (
          <span className="gpb-ed-tag-preview" aria-label={`${label} preview`}>
            <GiftPassTag tag={{ text: p.tag.text.trim(), color: p.tag.color }} />
          </span>
        )}
      </div>
      {p.error && <EditorFieldError id={errorId}>{p.error}</EditorFieldError>}
    </div>
  );
}
